import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Info } from 'lucide-react';

const AuthLayoutTutorial = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">AuthLayout</h1>
          <p className="text-lg text-muted-foreground">
            AuthLayout provides a centered card design for authentication pages like login, register, and password reset.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Visual Structure</h2>

          <Card>
            <CardContent className="pt-6">
              <div className="border border-border rounded-lg overflow-hidden bg-muted/30 min-h-[300px] flex items-center justify-center">
                <div className="bg-card border border-border rounded-lg shadow-lg p-6 w-80">
                  <div className="text-center mb-4">
                    <div className="w-10 h-10 bg-primary rounded-lg mx-auto mb-2"></div>
                    <div className="font-semibold">Sign In</div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-10 bg-muted rounded"></div>
                    <div className="h-10 bg-muted rounded"></div>
                    <div className="h-10 bg-primary rounded"></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Basic Usage</h2>

          <CodePreview
            title="Simple Auth Page"
            code={`import { AuthLayout } from '@voilajsx/uikit/auth-layout';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';

function LoginPage() {
  return (
    <AuthLayout
      logo={<Logo />}
      title="Welcome back"
      description="Sign in to your account"
    >
      <form className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" placeholder="you@example.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" />
        </div>
        <Button type="submit" className="w-full">Sign In</Button>
      </form>
    </AuthLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Props Reference</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Prop</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">logo</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Logo or brand element</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">title</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Page heading</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">description</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Subtitle text</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">footer</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Footer content (links, etc.)</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">maxWidth</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Card max width (default: "sm")</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Login Page Example</h2>

          <CodePreview
            title="Complete Login Page"
            code={`import { AuthLayout } from '@voilajsx/uikit/auth-layout';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';
import { Checkbox } from '@voilajsx/uikit/checkbox';
import { Link } from 'react-router-dom';

function LoginPage() {
  return (
    <AuthLayout
      logo={
        <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold text-xl">
          A
        </div>
      }
      title="Sign in"
      description="Enter your credentials to access your account"
      footer={
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:underline">
            Sign up
          </Link>
        </p>
      }
    >
      <form className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox id="remember" />
          <Label htmlFor="remember" className="text-sm font-normal">
            Remember me
          </Label>
        </div>

        <Button type="submit" className="w-full">
          Sign In
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or</span>
          </div>
        </div>

        <Button type="button" variant="outline" className="w-full">
          Continue with Google
        </Button>
      </form>
    </AuthLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Register Page Example</h2>

          <CodePreview
            title="Registration Form"
            code={`import { AuthLayout } from '@voilajsx/uikit/auth-layout';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';

function RegisterPage() {
  return (
    <AuthLayout
      logo={<Logo />}
      title="Create account"
      description="Start your 14-day free trial"
      footer={
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      <form className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" />
          <p className="text-xs text-muted-foreground">
            Must be at least 8 characters
          </p>
        </div>

        <Button type="submit" className="w-full">
          Create Account
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          By signing up, you agree to our{' '}
          <Link to="/terms" className="underline">Terms</Link> and{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </form>
    </AuthLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            AuthLayout automatically centers vertically and horizontally, has a subtle background pattern, and is fully responsive.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">With Background Image</h2>

          <CodePreview
            title="Split Layout Variant"
            code={`<AuthLayout
  logo={<Logo />}
  title="Welcome"
  backgroundImage="/auth-bg.jpg"  // Shows image on one side
  splitLayout={true}              // Side-by-side on desktop
>
  <form>...</form>
</AuthLayout>`}
            language="tsx"
          />
        </section>
      </div>
    </TutorialLayout>
  );
};

export default AuthLayoutTutorial;
